import { useState, useEffect, useMemo } from 'react';
import { cn } from '../../utils/cn';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

function calculateTimeLeft(targetDate: Date): TimeLeft {
  const difference = targetDate.getTime() - new Date().getTime();

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / (1000 * 60)) % 60),
    seconds: Math.floor((difference / 1000) % 60),
    total: difference,
  };
}

interface CountdownTimerProps {
  targetDate: Date | string;
  onComplete?: () => void;
  variant?: 'default' | 'compact' | 'cards';
  showDays?: boolean;
  showLabels?: boolean;
  className?: string;
}

export function CountdownTimer({
  targetDate,
  onComplete,
  variant = 'default',
  showDays = true,
  showLabels = true,
  className,
}: CountdownTimerProps) {
  const target = useMemo(
    () => (typeof targetDate === 'string' ? new Date(targetDate) : targetDate),
    [targetDate]
  );

  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => calculateTimeLeft(target));

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(target);
      setTimeLeft(newTimeLeft);

      if (newTimeLeft.total <= 0) {
        clearInterval(timer);
        onComplete?.();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [target, onComplete]);

  const isUrgent = timeLeft.total > 0 && timeLeft.total < 1000 * 60 * 60; // Less than 1 hour
  const isExpired = timeLeft.total <= 0;

  if (isExpired) {
    return (
      <div className={cn('text-error-500 font-medium', className)}>
        Expired
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div
        className={cn(
          'flex items-center gap-1 font-mono text-sm font-medium',
          isUrgent ? 'text-error-500' : 'text-gray-900 dark:text-white',
          className
        )}
      >
        {showDays && timeLeft.days > 0 && (
          <>
            <span>{String(timeLeft.days).padStart(2, '0')}</span>
            <span className="text-gray-400">:</span>
          </>
        )}
        <span>{String(timeLeft.hours).padStart(2, '0')}</span>
        <span className="text-gray-400">:</span>
        <span>{String(timeLeft.minutes).padStart(2, '0')}</span>
        <span className="text-gray-400">:</span>
        <span className={cn(isUrgent && 'animate-pulse')}>
          {String(timeLeft.seconds).padStart(2, '0')}
        </span>
      </div>
    );
  }

  if (variant === 'cards') {
    const timeUnits = [
      ...(showDays && timeLeft.days > 0 ? [{ value: timeLeft.days, label: 'Days' }] : []),
      { value: timeLeft.hours, label: 'Hours' },
      { value: timeLeft.minutes, label: 'Mins' },
      { value: timeLeft.seconds, label: 'Secs' },
    ];

    return (
      <div className={cn('flex items-center gap-2', className)}>
        {timeUnits.map((unit, index) => (
          <div key={unit.label} className="flex items-center">
            <div
              className={cn(
                'flex flex-col items-center justify-center',
                'min-w-[48px] px-2 py-2',
                'bg-dark-900 dark:bg-dark-800 rounded-lg',
                'border border-dark-700',
                isUrgent && 'border-error-500/50'
              )}
            >
              <span
                className={cn(
                  'text-xl font-bold font-mono',
                  isUrgent ? 'text-error-500' : 'text-white'
                )}
              >
                {String(unit.value).padStart(2, '0')}
              </span>
              {showLabels && (
                <span className="text-2xs text-gray-400 uppercase tracking-wide">
                  {unit.label}
                </span>
              )}
            </div>
            {index < timeUnits.length - 1 && (
              <span className="mx-1 text-xl font-bold text-gray-500">:</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Default variant
  const timeUnits = [
    ...(showDays && timeLeft.days > 0 ? [{ value: timeLeft.days, label: 'Days' }] : []),
    { value: timeLeft.hours, label: 'Hours' },
    { value: timeLeft.minutes, label: 'Minutes' },
    { value: timeLeft.seconds, label: 'Seconds' },
  ];

  return (
    <div className={cn('flex items-center gap-3', className)}>
      {timeUnits.map((unit, index) => (
        <div key={unit.label} className="flex items-center">
          <div className="text-center">
            <div
              className={cn(
                'text-2xl font-bold font-mono',
                isUrgent
                  ? 'text-error-500'
                  : 'text-gray-900 dark:text-white'
              )}
            >
              {String(unit.value).padStart(2, '0')}
            </div>
            {showLabels && (
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {unit.label}
              </div>
            )}
          </div>
          {index < timeUnits.length - 1 && (
            <span className="mx-2 text-2xl font-bold text-gray-300 dark:text-dark-600">
              :
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

interface DealCountdownProps {
  endTime: Date | string;
  label?: string;
  className?: string;
}

export function DealCountdown({
  endTime,
  label = 'Ends in',
  className,
}: DealCountdownProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-2',
        'bg-error-500/10 dark:bg-error-500/20 rounded-lg',
        'border border-error-500/20',
        className
      )}
    >
      <span className="text-sm font-medium text-error-600 dark:text-error-400">
        {label}
      </span>
      <CountdownTimer
        targetDate={endTime}
        variant="compact"
        showDays={false}
      />
    </div>
  );
}
